/**
 * Contact-form protection (Cloudflare Turnstile, managed mode).
 *
 * The public site key is a build-time value (VITE_TURNSTILE_SITE_KEY). Without
 * it the forms render and submit exactly as before. With it, each form shows
 * the verification widget and sends its single-use token in a request header,
 * so the request body stays unchanged.
 */
export const FORM_PROTECTION_TOKEN_HEADER = "X-Form-Protection-Token";

export function turnstileSiteKey(): string {
  return String(import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "").trim();
}

export type TurnstileApi = {
  render(container: HTMLElement, options: Record<string, unknown>): string | undefined;
  reset(widgetId?: string): void;
  remove(widgetId?: string): void;
};

const TURNSTILE_SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let loading: Promise<TurnstileApi> | null = null;

/** Loads Cloudflare's script once per page; a failed load can be retried. */
export function loadTurnstile(): Promise<TurnstileApi> {
  const scope = window as unknown as { turnstile?: TurnstileApi };
  if (scope.turnstile) return Promise.resolve(scope.turnstile);
  loading ??= new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT;
    script.async = true;
    script.onload = () => (scope.turnstile ? resolve(scope.turnstile) : reject(new Error("Verification did not load")));
    script.onerror = () => {
      script.remove();
      reject(new Error("Verification did not load"));
    };
    document.head.append(script);
  }).catch((error: unknown) => {
    loading = null;
    throw error;
  });
  return loading;
}

function newKey(): string {
  return globalThis.crypto?.randomUUID?.() ?? "";
}

/**
 * Keeps one Idempotency-Key per request body, so a retry of the same message
 * is not screened or sent twice. A changed message gets a new key.
 */
export function createAttempts() {
  let attempt: { body: string; key: string } | null = null;
  return {
    key(body: string): string {
      if (attempt?.body !== body) attempt = { body, key: newKey() };
      return attempt.key;
    },
    clear() {
      attempt = null;
    },
  };
}
